import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  Typography,
  Modal,
  Spin,
  DatePicker,
  notification,
  Card,
  Space,
  Button,
  Tag,
} from "antd";
import {
  ShoppingCartOutlined,
  DollarCircleOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileOutlined,
  UserOutlined,
  DownloadOutlined,
  CalendarOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import {
  useInventoryReportsServiceDownloadCustomerReport,
  useInventoryReportsServiceDownloadInventoryAgingReport,
  useInventoryReportsServiceDownloadInventoryLowStockAlertReport,
  useInventoryReportsServiceDownloadInventoryMovementReport,
  useInventoryReportsServiceDownloadInventoryValuationReport,
  useInventoryReportsServiceDownloadPurchaseOrdersReport,
  useInventoryReportsServiceDownloadVendorSummaryReport,
} from "api/queries";
import { Dayjs } from "dayjs";
import VendorSelect from "components/common/VendorSelect";
import CustomerSelect from "components/common/CustomerSelect";

const { RangePicker } = DatePicker;
const { Title, Text, Paragraph } = Typography;

// Define the report categories and their reports with enhanced styling
const reportCategories = ({
  onClick,
}: {
  onClick: (reportType: string) => void;
}) => ({
  inventory: {
    title: "Inventory Management",
    description: "Comprehensive inventory tracking and analysis reports",
    color: "#1890ff",
    icon: ShoppingCartOutlined,
    reports: [
      {
        title: "Warehouse Stock Levels",
        description:
          "Track stock levels across different warehouses and locations.",
        icon: ShoppingCartOutlined,
        tag: "Inventory",
        tagColor: "cyan",
        onClick: () => onClick("Warehouse Stock Levels"),
      },
      {
        title: "Warehouse Transfer Logs",
        description:
          "Monitor stock transfers between warehouses and track movement history.",
        icon: ShoppingCartOutlined,
        tag: "Operations",
        tagColor: "purple",
        onClick: () => onClick("Warehouse Transfer Logs"),
      },
      {
        title: "Low Stock Alert",
        description:
          "Identify products below warning level for proactive replenishment.",
        icon: FileOutlined,
        tag: "Alerts",
        tagColor: "red",
        onClick: () => onClick("Low Stock Alert"),
      },
      {
        title: "Out of Stock Products",
        description:
          "View all products currently out of stock across all locations.",
        icon: FileOutlined,
        tag: "Alerts",
        tagColor: "red",
        onClick: () => onClick("Out of Stock Products"),
      },
      {
        title: "Stock Valuation",
        description:
          "Total inventory value by buying price and selling price analysis.",
        icon: DollarCircleOutlined,
        tag: "Financial",
        tagColor: "green",
        onClick: () => onClick("Stock Valuation Report"),
      },
      {
        title: "Stock Movement Report",
        description:
          "Track inventory inflow/outflow per product via sales, purchases, and adjustments.",
        icon: ShoppingCartOutlined,
        tag: "Operations",
        tagColor: "orange",
        onClick: () => onClick("Stock Movement Report"),
      },
      // Keep existing reports
      {
        title: "Inventory Aging Report",
        description:
          "Categorize products based on how long they have been in stock.",
        icon: BarChartOutlined,
        tag: "Analytics",
        tagColor: "blue",
        onClick: () => onClick("Inventory Aging Report"),
      },
      {
        title: "Inventory Valuation Report",
        description:
          "Calculate the current value of inventory, including pending purchase orders.",
        icon: DollarCircleOutlined,
        tag: "Financial",
        tagColor: "green",
        onClick: () => onClick("Inventory Valuation Report"),
      },
      // {
      //   title: "Low Stock Alert Report",
      //   description:
      //     "Identify items at or below reorder levels for proactive procurement.",
      //   icon: FileOutlined,
      //   tag: "Alerts",
      //   tagColor: "red",
      //   onClick: () => onClick("Low Stock Alerts"),
      // },
    ],
  },
  sales: {
    title: "Sales Reports",
    description: "Comprehensive sales analysis and performance tracking",
    color: "#722ed1",
    icon: DollarCircleOutlined,
    reports: [
      {
        title: "Sales Report",
        description: "Detailed sales report with filters for date range.",
        icon: DollarCircleOutlined,
        tag: "Sales",
        tagColor: "blue",
        onClick: () => onClick("Sales Report"),
      },
      {
        title: "Sales Summary Report",
        description:
          "Daily, weekly, and monthly sales overview with total sales, invoices, and top-selling products.",
        icon: BarChartOutlined,
        tag: "Analytics",
        tagColor: "purple",
        onClick: () => onClick("Sales Summary Report"),
      },
      {
        title: "Sales by Product",
        description:
          "Detailed analysis of sales performance by product, including quantity sold and profit margins.",
        icon: ShoppingCartOutlined,
        tag: "Products",
        tagColor: "blue",
        onClick: () => onClick("Sales by Product"),
      },
      {
        title: "Sales by Customer",
        description:
          "Customer purchase history and analysis including total purchases and average invoice values.",
        icon: UserOutlined,
        tag: "Customers",
        tagColor: "cyan",
        onClick: () => onClick("Sales by Customer"),
      },
      {
        title: "Sales by Employee",
        description:
          "Employee sales performance tracking with invoice counts and revenue metrics.",
        icon: TeamOutlined,
        tag: "Performance",
        tagColor: "green",
        onClick: () => onClick("Sales by Employee"),
      },
      {
        title: "Invoice Aging Report",
        description:
          "Track overdue and upcoming invoices with detailed payment status.",
        icon: FileOutlined,
        tag: "Financial",
        tagColor: "orange",
        onClick: () => onClick("Invoice Aging Report"),
      },
      {
        title: "Reparation Case Summary",
        description: "Summary of reparation cases with status and totals.",
        icon: FileOutlined,
        tag: "Reparations",
        tagColor: "magenta",
        onClick: () => onClick("Reparation Case Summary"),
      },
    ],
  },
  accounting: {
    title: "Financial Reports",
    description: "Comprehensive financial analysis and accounting reports",
    color: "#52c41a",
    icon: DollarCircleOutlined,
    reports: [
      {
        title: "Balance Sheet",
        description:
          "Overview of company assets and liabilities with detailed breakdowns.",
        icon: FileOutlined,
        tag: "Financial",
        tagColor: "green",
        onClick: () => onClick("Balance Sheet"),
      },
      {
        title: "Profit & Loss Report",
        description:
          "Total Revenue, COGS (based on buying price), Gross Profit, Net Profit.",
        icon: DollarCircleOutlined,
        tag: "Financial",
        tagColor: "green",
        onClick: () => onClick("Profit & Loss Report"),
      },
      {
        title: "Daily/Monthly Transactions Report",
        description:
          "All cash ins/outs including sales, expenses, and purchases.",
        icon: FileOutlined,
        tag: "Transactions",
        tagColor: "blue",
        onClick: () => onClick("Daily/Monthly Transactions Report"),
      },
      {
        title: "Expense Report",
        description: "Expenses by category, date, and amount.",
        icon: FileOutlined,
        tag: "Expenses",
        tagColor: "red",
        onClick: () => onClick("Expense Report"),
      },
    ],
  },
  vendor: {
    title: "Vendor & Supplier Reports",
    description: "Track vendor performance and purchase order analytics",
    color: "#faad14",
    icon: TeamOutlined,
    reports: [
      {
        title: "Vendor Summary",
        description:
          "Comprehensive overview of vendor purchases and performance metrics.",
        icon: TeamOutlined,
        tag: "Analytics",
        tagColor: "gold",
        onClick: () => onClick("Vendor Summary"),
      },
      {
        title: "Purchase Orders",
        description:
          "Track vendor purchase orders with detailed status and performance analysis.",
        icon: ShoppingCartOutlined,
        tag: "Operations",
        tagColor: "orange",
        onClick: () => onClick("Purchase Orders"),
      },
    ],
  },
  customer: {
    title: "Customer Analytics",
    description: "Customer insights and relationship management reports",
    color: "#eb2f96",
    icon: UserOutlined,
    reports: [
      {
        title: "Customer Overview",
        description:
          "Detailed analysis of customer data, purchase patterns, and relationship insights.",
        icon: UserOutlined,
        tag: "CRM",
        tagColor: "magenta",
        onClick: () => onClick("Customer Overview"),
      },
    ],
  },
  purchases: {
    title: "Purchase Reports",
    description: "Detailed analytics and summaries for purchase operations",
    color: "#13c2c2",
    icon: ShoppingCartOutlined,
    reports: [
      {
        title: "Purchase Order Summary",
        description:
          "Summary of purchase orders including status, delivery, value, and profit.",
        icon: FileOutlined,
        tag: "Orders",
        tagColor: "cyan",
        onClick: () => onClick("Purchase Order Summary"),
      },
      {
        title: "Pending Delivery Purchase Orders",
        description: "List of purchase orders pending delivery.",
        icon: FileOutlined,
        tag: "Pending",
        tagColor: "orange",
        onClick: () => onClick("Pending Delivery Purchase Orders"),
      },
      // {
      //   title: "Purchase Summary",
      //   description: "Summary of items purchased, vendors, and costs.",
      //   icon: FileOutlined,
      //   tag: "Summary",
      //   tagColor: "blue",
      //   onClick: () => onClick("Purchase Summary"),
      // },
    ],
  },
  userReports: {
    title: "User Reports",
    description: "User-based analytics and system activity logs",
    color: "#4096ff",
    icon: UserOutlined,
    reports: [
      // {
      //   title: "Sales by User",
      //   description: "Revenue per user/account with sales breakdown.",
      //   icon: DollarCircleOutlined,
      //   tag: "User",
      //   tagColor: "blue",
      //   onClick: () => onClick("Sales by User"),
      // },
      {
        title: "System Log",
        description: "System activity and user action logs.",
        icon: FileOutlined,
        tag: "System",
        tagColor: "orange",
        onClick: () => onClick("System Log"),
      },
    ],
  },
});

const ReportDashboard = () => {
  const nav = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [vendorIdFilter, setVendorIdFilter] = useState("");
  const [orderStatusFilter] = useState("");
  const [customerIdFilter, setCustomerIdFilter] = useState("");

  // Report hooks (unchanged)
  const { mutate: downloadInventoryAgingReport } =
    useInventoryReportsServiceDownloadInventoryAgingReport();
  const { mutate: downloadInventoryValuationReport } =
    useInventoryReportsServiceDownloadInventoryValuationReport();
  const { mutate: downloadStockMovementReport } =
    useInventoryReportsServiceDownloadInventoryMovementReport();
  const { mutate: downloadLowStockAlertsReport } =
    useInventoryReportsServiceDownloadInventoryLowStockAlertReport();
  const { mutate: downloadVendorSummaryReport } =
    useInventoryReportsServiceDownloadVendorSummaryReport();
  const { mutate: downloadPurchaseOrdersReport } =
    useInventoryReportsServiceDownloadPurchaseOrdersReport();
  const { mutate: downloadCustomerReport } =
    useInventoryReportsServiceDownloadCustomerReport();

  const handleReportClick = (reportType: string) => {
    switch (reportType) {
      case "Current Stock Status":
        nav("/reports/stock-status");
        break;
      case "Warehouse Stock Levels":
        nav("/reports/warehouse-stock");
        break;
      case "Warehouse Transfer Logs":
        nav("/reports/warehouse-transfers");
        break;
      case "Low Stock Alert":
        nav("/reports/low-stock");
        break;
      case "Out of Stock Products":
        nav("/reports/out-of-stock");
        break;
      case "Stock Valuation Report":
      case "Stock Valuation":
        nav("/reports/stock-valuation");
        break;
      case "Stock Movement Report":
        nav("/reports/stock-movement");
        break;
      // Existing cases
      case "Sales Summary Report":
        nav("/reports/sales-summary");
        break;
      case "Sales Report":
        nav("/reports/sales-report");
        break;
      case "Sales by Product":
        nav("/reports/sales-by-product");
        break;
      case "Sales by Customer":
        nav("/reports/sales-by-customer");
        break;
      case "Sales by Employee":
        nav("/reports/sales-by-employee");
        break;
      case "Invoice Aging Report":
        nav("/reports/invoice-aging");
        break;
      case "Inventory Aging Report":
        setSelectedReport(reportType);
        setIsModalVisible(true);
        break;
      case "Low Stock Alerts":
        setLoading(true);
        downloadLowStockAlertsReport(undefined, {
          onSettled: () => setLoading(false),
          onSuccess: (data) => {
            if (data?.downloadUrl) {
              window.open(data.downloadUrl, "_blank");
            }
          },
          onError: (error?: any) => {
            notification.error({
              description:
                error?.body?.message ||
                error?.response?.data?.message ||
                error?.message,
              message: "Failed to download report",
            });
          },
        });
        break;
      case "Vendor Summary":
      case "Purchase Orders":
        setSelectedReport(reportType);
        setIsModalVisible(true);
        break;
      case "Purchase Order Summary":
        nav("/reports/purchases/order-summary");
        break;
      case "Pending Delivery Purchase Orders":
        nav("/reports/purchases/pending-delivery");
        break;
      case "Purchase Summary":
        nav("/reports/purchases/summary");
        break;
      case "Profit & Loss Report":
        nav("/reports/financial/profit-and-loss");
        break;
      case "Daily/Monthly Transactions Report":
        nav("/reports/financial/daily-monthly-transactions");
        break;
      case "Expense Report":
        nav("/reports/financial/expense-report");
        break;
      case "Sales by User":
        nav("/reports/user-reports/sales-by-user");
        break;
      case "System Log":
        nav("/reports/user-reports/system-log");
        break;
      case "Customer Overview":
        nav("/reports/customer-overview");
        break;
      case "Balance Sheet":
        nav("/reports/financial/balance-sheet");
        break;
      case "Reparation Case Summary":
        nav("/reports/reparation-case-summary");
        break;
      default:
        setSelectedReport(reportType);
        setIsModalVisible(true);
        break;
    }
  };

  const handleDateRangeChange = (dates: any) => {
    setDateRange(dates);
  };

  const handleModalOk = () => {
    if (!dateRange) {
      notification.error({
        message: "Date range required",
        description: "Please select a valid date range.",
      });
      return;
    }
    setIsModalVisible(false);
    const startDate = dateRange[0].format("YYYY-MM-DD");
    const endDate = dateRange[1].format("YYYY-MM-DD");
    setLoading(true);

    switch (selectedReport) {
      case "Inventory Aging Report":
        downloadInventoryAgingReport(
          { startDate, endDate },
          {
            onSettled: () => setLoading(false),
            onSuccess: (data) => {
              if (data?.downloadUrl) {
                window.open(data.downloadUrl, "_blank");
              }
            },
            onError: (error?: any) => {
              notification.error({
                description:
                  error?.body?.message ||
                  error?.response?.data?.message ||
                  error?.message,
                message: "Failed to download report",
              });
            },
          }
        );
        break;
      case "Inventory Valuation Report":
        downloadInventoryValuationReport(
          { startDate, endDate },
          {
            onSettled: () => setLoading(false),
            onSuccess: (data) => {
              if (data?.downloadUrl) {
                window.open(data.downloadUrl, "_blank");
              }
            },
            onError: (error?: any) => {
              notification.error({
                description:
                  error?.body?.message ||
                  error?.response?.data?.message ||
                  error?.message,
                message: "Failed to download report",
              });
            },
          }
        );
        break;
      case "Stock Movement Report":
        downloadStockMovementReport(
          { startDate, endDate },
          {
            onSettled: () => setLoading(false),
            onSuccess: (data) => {
              if (data?.downloadUrl) {
                window.open(data.downloadUrl, "_blank");
              }
            },
            onError: (error?: any) => {
              notification.error({
                description:
                  error?.body?.message ||
                  error?.response?.data?.message ||
                  error?.message,
                message: "Failed to download report",
              });
            },
          }
        );
        break;
      case "Vendor Summary":
        downloadVendorSummaryReport(
          { startDate, endDate, vendorId: vendorIdFilter },
          {
            onSettled: () => setLoading(false),
            onSuccess: (data: any) => {
              if (data?.downloadUrl) {
                window.open(data.downloadUrl, "_blank");
              }
            },
            onError: (error?: any) => {
              notification.error({
                description:
                  error?.body?.message ||
                  error?.response?.data?.message ||
                  error?.message,
                message: "Failed to download vendor summary report",
              });
            },
          }
        );
        break;
      case "Purchase Orders":
        downloadPurchaseOrdersReport(
          {
            startDate,
            endDate,
            vendorId: vendorIdFilter,
            status: orderStatusFilter,
          },
          {
            onSettled: () => setLoading(false),
            onSuccess: (data: any) => {
              if (data?.downloadUrl) {
                window.open(data.downloadUrl, "_blank");
              }
            },
            onError: (error?: any) => {
              notification.error({
                description:
                  error?.body?.message ||
                  error?.response?.data?.message ||
                  error?.message,
                message: "Failed to download purchase orders report",
              });
            },
          }
        );
        break;
      case "Customer Overview":
        downloadCustomerReport(
          { startDate, endDate, customerId: customerIdFilter },
          {
            onSettled: () => setLoading(false),
            onSuccess: (data: any) => {
              if (data?.downloadUrl) {
                window.open(data.downloadUrl, "_blank");
              }
            },
            onError: (error?: any) => {
              notification.error({
                description:
                  error?.body?.message ||
                  error?.response?.data?.message ||
                  error?.message,
                message: "Failed to download customer report",
              });
            },
          }
        );
        break;
      default:
        setLoading(false);
        break;
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const ReportCard = ({
    category,
    categoryKey,
  }: {
    category: any;
    categoryKey: string;
  }) => (
    <Card
      className="report-category-card"
      style={{
        marginBottom: 32,
        borderRadius: 12,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        border: "1px solid #f0f0f0",
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div
        style={{
          background: `linear-gradient(135deg, ${category.color}15, ${category.color}08)`,
          padding: "24px 24px 16px 24px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Space align="center" size={16}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: category.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <category.icon style={{ fontSize: 24, color: "white" }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: "#262626" }}>
              {category.title}
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>
              {category.description}
            </Text>
          </div>
        </Space>
      </div>

      <div style={{ padding: "16px 0" }}>
        <List
          itemLayout="horizontal"
          dataSource={category.reports}
          renderItem={(report: any) => (
            <List.Item
              style={{
                padding: "16px 24px",
                margin: 0,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
                borderRadius: 8,
                marginInline: 16,
              }}
              className="report-list-item"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fafafa";
                e.currentTarget.style.transform = "translateX(4px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.transform = "translateX(0)";
              }}
              onClick={report.onClick}
              actions={[
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  style={{ color: category.color, display: "none" }} // Hide the button
                >
                  Generate
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: `${category.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <report.icon
                      style={{ fontSize: 18, color: category.color }}
                    />
                  </div>
                }
                title={
                  <Space>
                    <Text strong style={{ fontSize: 16, color: "#262626" }}>
                      {report.title}
                    </Text>
                    <Tag color={report.tagColor} style={{ fontSize: 11 }}>
                      {report.tag}
                    </Tag>
                  </Space>
                }
                description={
                  <Text
                    style={{ fontSize: 14, color: "#8c8c8c", lineHeight: 1.4 }}
                  >
                    {report.description}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      </div>
    </Card>
  );

  const categories = reportCategories({ onClick: handleReportClick });

  return (
    <div
      style={{
        padding: "32px 40px",
        background: "#fafafa",
        minHeight: "100vh",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header Section */}
        <div
          style={{
            marginBottom: 40,
            textAlign: "center",
            background: "white",
            padding: "40px 32px",
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
        >
          <Title
            level={1}
            style={{
              margin: 0,
              marginBottom: 8,
              background: "linear-gradient(135deg, #1890ff, #722ed1)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: 36,
              fontWeight: 700,
            }}
          >
            Business Intelligence Dashboard
          </Title>
          <Paragraph
            style={{
              fontSize: 16,
              color: "#8c8c8c",
              margin: 0,
              maxWidth: 600,
              marginInline: "auto",
            }}
          >
            Generate comprehensive reports and insights across all business
            operations. Access real-time analytics and data-driven intelligence
            for informed decision making.
          </Paragraph>
        </div>

        {/* Loading Overlay */}
        <Spin
          spinning={loading}
          tip="Generating your report..."
          size="large"
          style={{
            position: loading ? "fixed" : "static",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            background: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Report Categories */}
          <div>
            {Object.entries(categories).map(([key, category]) => (
              <ReportCard key={key} category={category} categoryKey={key} />
            ))}
          </div>
        </Spin>

        {/* Enhanced Modal */}
        <Modal
          title={
            <div style={{ padding: "8px 0" }}>
              <Space>
                <CalendarOutlined style={{ color: "#1890ff" }} />
                <span style={{ fontSize: 18, fontWeight: 600 }}>
                  Configure Report: {selectedReport}
                </span>
              </Space>
            </div>
          }
          visible={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={560}
          okText={
            <Space>
              <DownloadOutlined />
              Generate Report
            </Space>
          }
          cancelText="Cancel"
          okButtonProps={{
            size: "large",
            style: { height: 44 },
          }}
          cancelButtonProps={{
            size: "large",
            style: { height: 44 },
          }}
          bodyStyle={{ padding: "24px 24px 16px" }}
        >
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <div>
              <Space style={{ marginBottom: 8 }}>
                <CalendarOutlined style={{ color: "#1890ff" }} />
                <Text strong>Date Range</Text>
                <Text type="danger">*</Text>
              </Space>
              <RangePicker
                onChange={handleDateRangeChange}
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 8,
                }}
                size="large"
                placeholder={["Start Date", "End Date"]}
              />
            </div>

            {/* Vendor filters */}
            {(selectedReport === "Vendor Summary" ||
              selectedReport === "Purchase Orders") && (
              <div>
                <Space style={{ marginBottom: 8 }}>
                  <FilterOutlined style={{ color: "#faad14" }} />
                  <Text strong>Vendor Filter</Text>
                </Space>
                <VendorSelect
                  isForm={false}
                  onChange={(e) => setVendorIdFilter(e)}
                  selectProps={{
                    style: { width: "100%" },
                    placeholder: "Select vendor (optional)",
                    size: "large",
                  }}
                />
              </div>
            )}

            {/* Customer filters */}
            {selectedReport === "Customer Overview" && (
              <div>
                <Space style={{ marginBottom: 8 }}>
                  <FilterOutlined style={{ color: "#eb2f96" }} />
                  <Text strong>Customer Filter</Text>
                </Space>
                <CustomerSelect
                  isForm={false}
                  onChange={(e) => setCustomerIdFilter(e)}
                  selectProps={{
                    style: { width: "100%" },
                    placeholder: "Select customer (optional)",
                    size: "large",
                  }}
                />
              </div>
            )}
          </Space>
        </Modal>
      </div>

      {/* <style jsx>{`
        .report-category-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
        }

        .report-list-item {
          border-left: 3px solid transparent;
        }

        .report-list-item:hover {
          border-left-color: currentColor;
        }
      `}</style> */}
    </div>
  );
};

export default ReportDashboard;
